import { useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { useToast } from '../../../components/ui/ToastProvider'
import { cx } from '../../../components/ui/cx'
import { useCartCount } from '../../../lib/cart/cart-count-context'
import { addCatalogResultToCart } from '../catalog-add-to-cart'
import { resultDisplayName, sourceBadgeLabel } from '../format'
import type { CatalogResultCard } from '../types'
import { CatalogResultThumb } from './CatalogResultThumb'
import { ConfiguratorDrawer } from './ConfiguratorDrawer'

/** Maps an availability `status` to the status-dot's color modifier class
 * (catalog.css) — success/warning/neutral, never color alone (the dot is
 * always paired with the note text, WCAG 1.4.1). */
const STATUS_DOT_CLASS: Record<string, string> = {
  in_stock: 'catalog-result-status-dot-success',
  made_to_order: 'catalog-result-status-dot-warning',
  unknown: 'catalog-result-status-dot-neutral',
}

/**
 * One catalog search result card (UID-32/UID-227/UID-33/UID-253/UID-34).
 * UID-253 adds the storefront's product-card treatment on top of the UID-32
 * payload (price · availability · config · provenance · image): a
 * source-badge pill overlaid on the image, a colored stock/lead-time status
 * dot, and an inline "Add" button on the price row.
 *
 * UID-34 (ADR-0097 Decision 1, Surface B entry point): when the card is
 * `kind='configurable'` (i.e. it carries a `configSchema`), the price row
 * shows "Configure" instead of "Add" — the family-level `indicativePrice` is
 * a disclosed placeholder (ADR-0097 §2), never a real per-combination price,
 * so showing it next to a direct-add action would misrepresent it. Clicking
 * "Configure" opens `ConfiguratorDrawer` (the same configurator core as
 * Surface A, ADR-0097 8.2), which resolves the picked value's REAL price via
 * the backend before any "Add to Cart" is possible. Non-configurable
 * (`kind='catalog'`) cards are completely unaffected — same "Add" as before.
 *
 * `layout` (UID-34c / visual-fidelity pass, catalog-series-row-expand-
 * visual-fidelity): `'grid'` (default) is the original grid-tile card used
 * by the search/browse result grids — unchanged. `'row'` is a horizontal
 * presentation for the series screen's table row-expand context
 * (`CatalogSeriesConfigurePage`'s `catalog-series-row-expanded` drawer
 * already supplies its own teal border/shadow "card" chrome around this
 * component, so the row variant drops its own outer border/background/
 * padding rather than nesting a card inside a card) — same fixed-thumbnail
 * + content-column shape as the design's `_realTable` row-expand mock. No
 * new fields are introduced for this layout; it renders exactly the same
 * real data as the grid tile, only rearranged.
 *
 * `'agent'` (UID-36 design-fidelity pass) is the sourcing-agent shortlist's
 * own compact row treatment (2026-08-23 handoff, README §7 screen 2): a
 * small thumbnail, the source badge INLINE next to the name (never
 * overlaid on the image the way `'grid'`/`'row'` render it), and the
 * price/Add stacked in a right-aligned column instead of sharing a row with
 * the price. Scoped to this one value so it never touches the grid/series
 * treatments above.
 */
export function ResultCard({
  card,
  layout = 'grid',
  onConfigureClick,
}: {
  readonly card: CatalogResultCard
  readonly layout?: 'grid' | 'row' | 'agent'
  /**
   * UID-460 inline resolution — when provided, "Configure" calls this
   * instead of opening this card's own `ConfiguratorDrawer` (which is not
   * even mounted in that case), so a caller that already owns an open
   * surface (e.g. `SeriesConfiguratorDrawer`'s multi-candidate list) can
   * reveal `ConfiguratorCore` inline in place rather than stacking a second
   * dialog on top of its own. Omitted (every pre-existing caller): behavior
   * is byte-identical to before this prop existed.
   */
  readonly onConfigureClick?: () => void
}) {
  const { sourceable, availability, configSchema } = card
  const name = resultDisplayName(sourceable)
  const badge = sourceBadgeLabel(sourceable.provenance)
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const [adding, setAdding] = useState(false)
  const [configuratorOpen, setConfiguratorOpen] = useState(false)
  const isAgent = layout === 'agent'

  function handleConfigureClick() {
    if (onConfigureClick) {
      onConfigureClick()
    } else {
      setConfiguratorOpen(true)
    }
  }

  async function handleAdd() {
    setAdding(true)
    try {
      const result = await addCatalogResultToCart(sourceable)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${name} to cart`)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    } finally {
      setAdding(false)
    }
  }

  return (
    <article
      className={cx(
        'catalog-result-card',
        layout === 'row' && 'catalog-result-card-row',
        isAgent && 'catalog-result-card-agent',
      )}
    >
      <div className="catalog-result-image-wrap">
        <CatalogResultThumb
          imageRef={sourceable.imageRef}
          name={name}
          {...(layout === 'row' ? { className: 'catalog-result-image-row' } : {})}
          {...(isAgent ? { className: 'catalog-result-image-agent' } : {})}
        />
        {/* The agent layout puts the badge inline next to the name below,
         * never overlaid on the thumbnail. */}
        {!isAgent && <span className="catalog-result-badge">{badge}</span>}
      </div>
      <div className="catalog-result-body">
        <div className={cx('catalog-result-name-row', isAgent && 'catalog-result-name-row-agent')}>
          <h2 className="catalog-result-name">{name}</h2>
          {isAgent && (
            <span
              className={cx(
                'catalog-result-badge-inline',
                badge === 'MISUMI'
                  ? 'catalog-result-badge-inline-misumi'
                  : 'catalog-result-badge-inline-fictiv',
              )}
            >
              {badge}
            </span>
          )}
        </div>
        {sourceable.catalogSku && <p className="catalog-result-sku">{sourceable.catalogSku}</p>}
        <p className="catalog-result-status">
          <span
            className={cx(
              'catalog-result-status-dot',
              STATUS_DOT_CLASS[availability.status] ?? STATUS_DOT_CLASS.unknown,
            )}
            aria-hidden="true"
          />
          {availability.note}
        </p>
        <div
          className={cx('catalog-result-price-row', isAgent && 'catalog-result-price-row-agent')}
        >
          {configSchema ? (
            <>
              <p className="catalog-result-configure-price">Configure for pricing</p>
              <Button type="button" size="sm" variant="primary" onClick={handleConfigureClick}>
                Configure
              </Button>
            </>
          ) : (
            <>
              {sourceable.indicativePrice ? (
                <p className="catalog-result-price">
                  {sourceable.indicativePrice.currency} {sourceable.indicativePrice.amount}
                </p>
              ) : (
                <p className="catalog-result-price catalog-result-price-quote">Quote required</p>
              )}
              <Button
                type="button"
                size="sm"
                variant="primary"
                loading={adding}
                onClick={() => void handleAdd()}
              >
                Add
              </Button>
            </>
          )}
        </div>
      </div>
      {configSchema && !onConfigureClick && (
        <ConfiguratorDrawer
          open={configuratorOpen}
          onClose={() => setConfiguratorOpen(false)}
          card={card}
        />
      )}
    </article>
  )
}
